import React from 'react'
// import './styles/notification-schedule-builder.scss'

import { getClassNames } from '../../../_utils/classnames'
import { Well } from '@pushly/aqe/lib/components'
import { NotificationDeliveryModel } from '../../../models/notification/notification-delivery.model'
import { NotificationDeliveryType } from '../../../enums/notification-delivery-type'
import { NotificationDeliveryWindow } from '../../../enums/notification-delivery-window'
import { DomainDto } from '../../../dtos/domain'
import { getSupportedTimezones } from '../../../_utils/moment'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { isContentTest, isDeliveryTest, isSingleDelivery } from '../helpers'
import { AccountDto } from '../../../dtos/account.dto'
import { NotificationBuilderLevel } from '../enums'
import { AppState } from '../../../stores/app'
import { Container } from 'typescript-ioc'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { ConfigProvider, DatePicker, Form, Popover, Radio, Select, Skeleton, Space, Tooltip, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'

interface INotificationDelivery {
    className?: string
    builder: INotificationBuilderState
    value: NotificationDeliveryModel
    onChange?: (value: NotificationDeliveryModel) => any
    domain: DomainDto
    account?: AccountDto
    loading?: boolean
    disabled?: boolean
    hideTypeLabel?: boolean
    defaultTimeZone?: string
}

const parseDateTimeInTz = (dt: string | undefined, tz: string): Dayjs | null | undefined => {
    return !dt ? undefined : dayjs(dt).tz(tz)
}

const DisabledComplexDeliveryWrapper: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<any>>> = (
    props,
) => (
    <Popover
        classNames={{
            root: getClassNames('popover'),
            content: getClassNames('notification-builder-popover', 'disabled-option-popover'),
        }}
        content="Delivery strategy not compatible with Sample Distribution."
    >
        {props.children}
    </Popover>
)

const NotificationDeliveryBuilder = (props: INotificationDelivery) => {
    const { className, loading, builder, value, onChange, disabled, hideTypeLabel, defaultTimeZone } = props

    const appState = Container.get(AppState)

    const level = builder.level ?? NotificationBuilderLevel.DOMAIN

    const isOrgLevel = level === NotificationBuilderLevel.ORG
    const isDomainLevel = level === NotificationBuilderLevel.DOMAIN
    const isJourneyLevel = level === NotificationBuilderLevel.CAMPAIGN
    const isJourneyOrDomainLevel = isJourneyLevel || isDomainLevel

    const userPreferences = useUserPreferences(
        appState.currentUser?.id,
        isJourneyOrDomainLevel ? props.domain.id : undefined,
        isOrgLevel ? props.account?.id : undefined,
    )

    const testTypes = builder.test?.getTestTypes()
    const isSampleDelivery = builder.test?.getDistributionType() === 'sample'
    const disableComplexDelivery = isContentTest(testTypes) && isSingleDelivery(testTypes) && isSampleDelivery
    const ComplexDeliveryWrapper = !disableComplexDelivery ? (_: any) => _.children : DisabledComplexDeliveryWrapper

    const now = dayjs()
    const tzOptions = getSupportedTimezones()
    const workingModel = value.clone()

    const isInitialCreate = builder.mode === 'create' && !builder.initialNotificationStateLoaded

    if (isInitialCreate) {
        const prefType = userPreferences.get(['notification_delivery_type'])
        const prefStrategy = userPreferences.get(['notification_delivery_strategy'])

        if (prefType && prefType !== workingModel.getType()) {
            workingModel.setType(prefType)
        }

        if (
            prefStrategy &&
            prefStrategy !== NotificationDeliveryWindow.INFORMED &&
            prefStrategy !== workingModel.getWindow()
        ) {
            workingModel.setWindow(prefStrategy)
        }
    }

    const defaultTZ = userPreferences.get(['last_selected_delivery_time_zone']) ?? defaultTimeZone ?? dayjs.tz.guess()
    const workingTz =
        (isInitialCreate ? userPreferences.get(['last_selected_delivery_time_zone']) : undefined) ??
        workingModel.getTimeZone() ??
        defaultTZ
    const workingSendDate = parseDateTimeInTz(workingModel.getSendDate(), workingTz)

    const applyTz = (model: NotificationDeliveryModel, tz: string) => {
        model.setTimeZone(tz)

        const newDate = parseDateTimeInTz(model.getSendDate() ?? new Date().toString(), workingTz)!
        // @ts-ignore
        newDate.tz(tz, true)

        model.setSendDate(newDate.toISOString())

        return model
    }

    const applyTime = (model: NotificationDeliveryModel, time: dayjs.Dayjs | undefined | null) => {
        time = (!time ? now : time) as dayjs.Dayjs

        const oldDate = parseDateTimeInTz(model.getSendDate(), workingTz)!
        const newDate = oldDate.hour(time.hour()).minute(time.minute())

        model.setSendDate(newDate.toISOString())

        return model
    }

    const handleTimeChange = (v: dayjs.Dayjs | null) => {
        const update = workingModel.clone()
        applyTime(update, v)

        onChange?.(update)
    }

    // Informed delivery has been removed from the platform: the option is offered only for a
    // notification that was loaded with it (edit or duplicate), so it can be switched back after
    // trying another strategy. The builder state records the loaded window, so neither the
    // user's edits nor a remount of this component change the answer.
    const canShowInformedDeliveryOption = builder.loadedDeliveryWindow === NotificationDeliveryWindow.INFORMED

    // While HOLD_OUT has been removed from the platform, we should continue
    // to show the option for A/B tests that have already been created with it.
    const canShowHoldOutDeliveryOption =
        builder.mode === 'edit' && (isDeliveryTest(testTypes) || workingModel.isUsingHoldOutStrategy)

    const isLiftTest = builder.variants?.reduce((acc, currentValue) => {
        if (currentValue.getDelivery().isUsingHoldOutStrategy) {
            acc = true
        }

        return acc
    }, false)

    const renderStrategyDescription = (param: NotificationDeliveryModel) => {
        if (param.isUsingFixedStrategy) {
            if (param.isUsingImmediateDelivery) {
                return (
                    <Typography.Paragraph type="secondary">
                        The notification will be delivered to the audience immediately.
                    </Typography.Paragraph>
                )
            } else {
                return (
                    <Typography.Paragraph type="secondary">
                        The notification will be delivered to the audience at the specified date and time.
                    </Typography.Paragraph>
                )
            }
        } else if (param.isUsingStzStrategy) {
            if (param.isUsingImmediateDelivery) {
                return (
                    <Typography.Paragraph type="secondary">
                        The notification will be delivered at the most likely time the subscriber will interact with it.
                        Delivery may occur at any time within the next 24 hours.
                    </Typography.Paragraph>
                )
            } else {
                return (
                    <Typography.Paragraph type="secondary">
                        The notification will be delivered to each subscriber at the specified date and time in their
                        time zone.
                    </Typography.Paragraph>
                )
            }
        } else if (param.isUsingHoldOutStrategy) {
            if (param.isUsingImmediateDelivery) {
                return (
                    <Typography.Paragraph type="secondary">
                        The percentage of subscribers configured for this variant will not receive the notification. Use
                        this option to perform a lift test to better understand the causal impact of this notification.
                    </Typography.Paragraph>
                )
            } else {
                return (
                    <Typography.Paragraph type="secondary">
                        The percentage of subscribers configured for this variant will not receive the notification. Use
                        this option to perform a lift test to better understand the causal impact of this notification.
                        The hold out audience will be calculated at the specified date and time.
                    </Typography.Paragraph>
                )
            }
            // Informed Strategy
        } else {
            if (param.isUsingImmediateDelivery) {
                return (
                    <Typography.Paragraph type="secondary">
                        The notification will be delivered at the most likely time the subscriber will interact with it.
                        Delivery may occur at any time within the next 24 hours.
                    </Typography.Paragraph>
                )
            } else {
                return (
                    <Typography.Paragraph type="secondary">
                        The notification will be delivered at the most likely time the subscriber will interact with it.
                        Delivery may occur at any time within the ensuing 24 hours after the specified date and time.
                    </Typography.Paragraph>
                )
            }
        }
    }

    return (
        <Skeleton loading={loading} active={true} avatar={false} title={false}>
            <ConfigProvider
                theme={{
                    components: {
                        Form: {
                            itemMarginBottom: 12,
                        },
                    },
                }}
            >
                <Form layout="vertical">
                    <Form.Item className="delivery-type-picker" label={hideTypeLabel ? undefined : 'Delivery Type'}>
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            onChange={(ev) => {
                                const update = workingModel.clone()
                                const type = ev.target.value
                                update.setType(type)
                                update.setTimeZone(defaultTZ)
                                update.setSendDate(now.format())

                                if (type === NotificationDeliveryType.IMMEDIATE) {
                                    if (!update.isUsingInformedStrategy) {
                                        update.setWindow(NotificationDeliveryWindow.STANDARD)
                                    }
                                } else {
                                    update.setSendDate(dayjs(now).tz(defaultTZ).add(15, 'minute').format())
                                }

                                onChange?.(update)
                            }}
                            disabled={disabled}
                            value={workingModel.getType()}
                            options={[
                                { label: 'Immediate', value: NotificationDeliveryType.IMMEDIATE },
                                { label: 'Scheduled', value: NotificationDeliveryType.SCHEDULED },
                            ]}
                            optionType="button"
                        />
                    </Form.Item>

                    <Form.Item label="Delivery Strategy" extra={renderStrategyDescription(workingModel)}>
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            disabled={disabled}
                            value={workingModel.getWindow()}
                            onChange={(ev) => {
                                const strategy = ev.target.value

                                const update = workingModel.clone()
                                update.setWindow(strategy)

                                if (!update.isUsingImmediateDelivery) {
                                    applyTz(update, defaultTZ)

                                    if (update.isUsingInformedStrategy) {
                                        applyTime(update, now.clone().hour(0).minute(0))
                                    } else if (workingModel.isUsingInformedStrategy) {
                                        applyTime(update, now.clone().add(15, 'minute'))
                                    }
                                }

                                onChange?.(update)
                            }}
                        >
                            <Radio.Button value={NotificationDeliveryWindow.STANDARD}>
                                {!workingModel.isUsingImmediateDelivery ? 'Fixed' : 'Now'}
                            </Radio.Button>
                            <ComplexDeliveryWrapper>
                                {!workingModel.isUsingImmediateDelivery && (
                                    <Radio.Button
                                        value={NotificationDeliveryWindow.TIMEZONE}
                                        disabled={disableComplexDelivery}
                                    >
                                        Subscriber Time Zone
                                    </Radio.Button>
                                )}
                                {canShowInformedDeliveryOption && (
                                    <Radio.Button
                                        value={NotificationDeliveryWindow.INFORMED}
                                        disabled={disableComplexDelivery}
                                    >
                                        Informed
                                    </Radio.Button>
                                )}
                                {canShowHoldOutDeliveryOption &&
                                    (!(
                                        isLiftTest &&
                                        builder.variants?.length > 1 &&
                                        workingModel.getWindow() !== NotificationDeliveryWindow.HOLD_OUT
                                    ) ? (
                                        <Radio.Button value={NotificationDeliveryWindow.HOLD_OUT}>
                                            Hold Out
                                        </Radio.Button>
                                    ) : (
                                        <Tooltip title="Only one variant in an A/B Test can use the “Hold Out” delivery strategy.">
                                            <Radio.Button
                                                value={NotificationDeliveryWindow.HOLD_OUT}
                                                disabled={
                                                    isLiftTest &&
                                                    builder.variants?.length > 1 &&
                                                    workingModel.getWindow() !== NotificationDeliveryWindow.HOLD_OUT
                                                }
                                            >
                                                Hold Out
                                            </Radio.Button>
                                        </Tooltip>
                                    ))}
                            </ComplexDeliveryWrapper>
                        </Radio.Group>
                    </Form.Item>

                    {!workingModel.isUsingImmediateDelivery && (
                        <Form.Item
                            className="delivery-senddate"
                            label={workingModel.isUsingInformedStrategy ? 'Earliest Delivery' : 'Schedule Time'}
                        >
                            <Space className="delivery-senddate-picker">
                                <DatePicker
                                    className="delivery-date-picker"
                                    format="ddd, MMM DD, YYYY"
                                    placeholder="Send Date"
                                    disabled={disabled}
                                    // @ts-ignore
                                    value={workingSendDate}
                                    onChange={(v) => {
                                        const update = workingModel.clone()

                                        if (!v) {
                                            update.setSendDate(undefined)
                                            update.setTimeZone(defaultTZ)
                                        } else {
                                            const oldDate =
                                                parseDateTimeInTz(workingSendDate?.toISOString(), workingTz) ?? v
                                            v.hour(!workingSendDate ? now.hour() : oldDate.hour())
                                            v.minute(!workingSendDate ? now.minute() : oldDate.minute())

                                            update.setSendDate(v.toISOString())
                                        }

                                        onChange?.(update)
                                    }}
                                />

                                <BetterTimePicker
                                    className="delivery-time-picker"
                                    format="h:mm a"
                                    use12Hours={true}
                                    placeholder="Time"
                                    disabled={disabled || !workingSendDate}
                                    // @ts-ignore
                                    value={workingSendDate ?? undefined}
                                    onTimeChange={handleTimeChange}
                                />

                                {!workingModel.isUsingStzStrategy && (
                                    <Select
                                        className="delivery-tz-picker"
                                        dropdownClassName={getClassNames('delivery-tz-picker-overlay')}
                                        disabled={disabled || !workingSendDate}
                                        showSearch={true}
                                        placeholder="Select a time zone"
                                        aria-label="Select delivery time zone"
                                        value={workingTz}
                                        onChange={(tz) => {
                                            const update = workingModel.clone()
                                            applyTz(update, tz)

                                            userPreferences.set(['last_selected_delivery_time_zone'], tz)

                                            onChange?.(update)
                                        }}
                                        options={tzOptions.map((tz) => ({ key: tz, value: tz, label: tz }))}
                                    />
                                )}
                            </Space>
                        </Form.Item>
                    )}
                </Form>
            </ConfigProvider>
        </Skeleton>
    )
}

export default NotificationDeliveryBuilder
